import { translate } from "@/i18n";
import { settingsShortcutsMessages } from "@/i18n/messages/settings-shortcuts";
import {
  claudeSettingName,
  claudeSettingPath,
  type ClaudeAppSetting,
  type ClaudeProfile,
  type ClaudeShortcutClash,
  type ClaudeTurnOff,
} from "@/lib/claude-app-shortcuts";
import { describeShortcut, shortcutPlatform } from "@/lib/shortcut-registry";
import { useClaudeAppShortcutStore } from "@/stores/claude-app-shortcut-store";
import { useNotificationStore } from "@/stores/notification-store";
import type { SystemToast } from "@/stores/notification-types";

/** The running-Claude notice's button: restart Claude and turn the key off. */
export const restartClaudeEvent = (setting: ClaudeAppSetting) =>
  `denker://claude-shortcut-restart/${setting}`;
export const runningToastId = (setting: ClaudeAppSetting) =>
  `claude-shortcut-running:${setting}`;

/**
 * Which Claude profiles, to follow "Claude" in a message: empty unless
 * Claude has more than one profile here.
 */
export function claudeProfilesText(profiles: ClaudeProfile[]): string {
  const names = profiles.flatMap((profile) => profile.name ?? []);
  return names.length
    ? translate(settingsShortcutsMessages, "claudeProfiles", {
        names: names.join(", "),
      })
    : "";
}

const keyboardToast = (
  toast: Omit<SystemToast, "level" | "icon" | "twoLineDetail">,
): SystemToast => ({
  ...toast,
  level: "info",
  icon: { kind: "icon", name: "Keyboard" },
  twoLineDetail: true,
});

/**
 * Claude is open, so its settings cannot change without a restart; or a
 * restart the user asked for did not turn the key off (`failed`). The
 * restart stays on offer while Claude is still running.
 */
export function claudeRunningToast(
  clash: ClaudeShortcutClash,
  failed?: ClaudeTurnOff | "error",
): SystemToast {
  const restart = !failed || failed === "claudeRunning";
  return keyboardToast({
    id: runningToastId(clash.claude.setting),
    title: translate(settingsShortcutsMessages, "claudeClashTitle"),
    description: failed
      ? turnOffFailedText(clash, failed)
      : translate(settingsShortcutsMessages, "claudeRunningToast", {
          profiles: claudeProfilesText(clash.claude.profiles),
          shortcut: describeShortcut(clash.claude),
        }),
    ...(restart && {
      actionLabel: translate(settingsShortcutsMessages, "restartClaudeTurnOff"),
      actionEvent: restartClaudeEvent(clash.claude.setting),
      actionTestId: "claude-shortcut-restart",
      actionsBelow: true,
    }),
  });
}

/** Claude's key is off now in the profiles `off`. */
function turnedOffToast(
  clash: ClaudeShortcutClash,
  off: ClaudeProfile[],
): SystemToast {
  return keyboardToast({
    // Not a clash id: it stays up after the clash is gone.
    id: `claude-shortcut-off:${clash.claude.setting}`,
    title: translate(settingsShortcutsMessages, "claudeOffTitle"),
    description: translate(settingsShortcutsMessages, "claudeOffToast", {
      profiles: claudeProfilesText(off),
      setting: claudeSettingName(clash.claude.setting, shortcutPlatform()),
      shortcut: describeShortcut(clash.claude),
    }),
  });
}

/** Why the key is still on, when the user asked to turn it off. */
export function turnOffFailedText(
  clash: ClaudeShortcutClash,
  result: ClaudeTurnOff | "error",
): string {
  return result === "claudeRunning"
    ? translate(settingsShortcutsMessages, "claudeQuitFailed")
    : translate(settingsShortcutsMessages, "claudeOffFailed", {
        path: claudeSettingPath(clash.claude.setting, shortcutPlatform()),
      });
}

const turnedOff = (result: ClaudeTurnOff | "error") =>
  result === "turnedOff" || result === "restarted";

/** When a key is on in several profiles, the outcome that matters most. */
const OUTCOME_ORDER: Array<ClaudeTurnOff | "error"> = [
  "claudeRunning",
  "quitUnchanged",
  "error",
  "alreadyOnce",
  "unsupported",
  "restarted",
  "turnedOff",
];

export type ClaudeTurnOffOutcome = {
  /** The outcome that matters most across the clash's profiles. */
  result: ClaudeTurnOff;
  /** The clash in the profiles whose Claude is still running. */
  running: ClaudeShortcutClash;
};

/**
 * Turn Claude's clashing key off in every profile it is on, one at a time,
 * and say so. A profile whose Claude quit for it is opened again natively.
 * `automatic` is Denker acting on its own (see the store's `turnOff`).
 * Throws when a profile's file cannot be changed and nothing else is
 * pending.
 */
export async function turnOffClaudeShortcut(
  clash: ClaudeShortcutClash,
  automatic: boolean,
): Promise<ClaudeTurnOffOutcome> {
  const { setting, profiles } = clash.claude;
  const results: Array<ClaudeTurnOff | "error"> = [];
  for (const profile of profiles)
    results.push(
      await useClaudeAppShortcutStore
        .getState()
        .turnOff(setting, profile.dir, automatic)
        .catch(() => "error" as const),
    );
  const where = (keep: (result: ClaudeTurnOff | "error") => boolean) =>
    profiles.filter((_, index) => keep(results[index]!));
  const off = where(turnedOff);
  const stillOpen = where(
    (result) => result === "claudeRunning" || result === "quitUnchanged",
  );
  const toasts = useNotificationStore.getState();
  if (off.length) {
    if (!stillOpen.length) toasts.removeSystemToast(runningToastId(setting));
    toasts.addSystemToast(() => turnedOffToast(clash, off));
  }
  const result =
    OUTCOME_ORDER.find((outcome) => results.includes(outcome)) ?? "unsupported";
  if (result === "error")
    throw new Error("Claude's settings could not be changed");
  return {
    result,
    running: stillOpen.length
      ? { ...clash, claude: { ...clash.claude, profiles: stillOpen } }
      : clash,
  };
}
